import GiftReggie from './giftreggie';

export default class GiftReggieService {
  constructor() {
    const technicalDataEl = document.querySelector('[data-technical]');
    this.cachedWishlist = window.localStorage.getItem('wishlist');
    this.sessionPromise = null;
    this.singleton = null;
    this.loadPromise = null;
    this.data = JSON.parse(technicalDataEl.textContent);
    this.customer = this.data.customer;
  }

  /**
   * Get wishlist session
   *
   * @returns {Promise<object>} Promise to wishlist session
   */
  getSession = () => {
    if (!this.sessionPromise) {
      this.sessionPromise = new Promise((resolve, reject) => {
        if (!this.customer) {
          reject(new Error('Customer is not logged in.'));
        }

        const { id, email, wishlist: key } = this.customer;
        GiftReggie.getSession(id, email, key)
          .then((session) => {
            resolve(session);
          })
          .fail(() => {
            reject(new Error('Failed to start session.'));
          });
      });
    }
    return this.sessionPromise;
  };

  /**
   * Get customer wishlist
   *
   * @returns {Promise<object>} Promise to wishlist object
   */
  getWishlist = ({ skipCache = false } = {}) => {
    if (!this.loadPromise) {
      this.loadPromise = new Promise((resolve, reject) => {
        if (this.singleton && !skipCache) {
          this.loadPromise = null;
          resolve(this.singleton);
        }

        if (this.cachedWishlist && !skipCache) {
          try {
            this.singleton = JSON.parse(this.cachedWishlist);
            this.getSession().then(() => {
              this.loadPromise = null;
              resolve(this.singleton);
            });
          } catch (e) {
            window.console.warn(
              'Unable to read wishlist from local storage',
              e,
            );
          }
        }

        if (!this.singleton || skipCache) {
          this.getSession()
            .then((session) =>
              session
                .getWishlist()
                .then(({ wishlist }) => {
                  this.singleton = wishlist;
                  this.setWishlist();
                  this.loadPromise = null;
                  resolve(this.singleton);
                })
                .fail((e) => {
                  if (e.status === 403) {
                    // Delete current user's session in case where current session is stale
                    GiftReggie.deleteCookie('giftreggie_session_cookie');
                    const { id, email, wishlist: key } = this.customer;
                    GiftReggie.reinitSession(email, id, key).then(
                      (currentSession) => {
                        currentSession.getWishlist();
                      },
                    );
                  } else if (e.status === 404) {
                    // Wishlist doesn't exist anymore, so need to open/create it first
                    session.openWishlist().then(() => {
                      const storedVariant =
                        parseInt(sessionStorage.getItem('storedVariant'), 10) ||
                        '';
                      // If user selected any variant to add to wishlist previously
                      if (storedVariant) {
                        session
                          .addWishlist(storedVariant)
                          .then(() => {
                            session.getWishlist();
                            sessionStorage.removeItem('storedVariant');
                          })
                          .fail((eCurrent) => {
                            const err = JSON.parse(eCurrent.response);
                            console.log(err.error); // eslint-disable-line no-console

                            session.getWishlist();
                            sessionStorage.removeItem('storedVariant');
                          });
                      } else {
                        session.getWishlist();
                      }
                    });
                  }
                  reject(new Error('Failed to get wishlist.'));
                }),
            )
            .catch((error) => {
              window.console.error(error);
              this.loadPromise = null;
              resolve({ products: [] });
            });
        }
      });
    }
    return this.loadPromise;
  };

  setWishlist = () => {
    window.localStorage.setItem('wishlist', JSON.stringify(this.singleton));
  };

  /**
   * Add variant to wishlist
   *
   * @param {string|number} variantId - Variant ID to add to wishlist
   * @returns {Promise<object>} Promise to wishlist object
   */
  addToWishlist = (variantId) =>
    new Promise((resolve, reject) => {
      this.getSession()
        .then((session) =>
          session
            .addWishlist(variantId)
            .then(async ({ wishlist }) => {
              this.singleton = wishlist;
              this.setWishlist();
              // await updateCart();
              resolve(this.singleton);
            })
            .fail(() => {
              reject(new Error('Failed to add to wishlist.'));
            }),
        )
        .catch((error) => reject(error));
    });

  /**
   * Remove variant from wishlist
   *
   * @param {string|number} variantId - Variant ID to remove from wishlist
   * @returns {Promise<object>} Promise to wishlist object
   */
  removeFromWishlist = (variantId) =>
    new Promise((resolve, reject) => {
      this.getSession()
        .then((session) =>
          session
            .removeWishlist(variantId)
            .then(({ wishlist }) => {
              this.singleton = wishlist;
              this.setWishlist();
              // updateCart();
              resolve(this.singleton);
            })
            .fail(() => {
              reject(new Error('Failed to remove from wishlist.'));
            }),
        )
        .catch((error) => reject(error));
    });
}
